import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { formatPortalDateTime } from '../SubcontractorPortal/portalShared'

const THREAD_TYPE_LABELS = {
  vendor: 'All crews',
  crew: 'Crew',
  direct: 'Direct',
  job: 'Job'
}

const useStyles = makeStyles((theme) => ({
  wrap: {
    border: '1px solid #e2e8e4',
    borderRadius: 16,
    padding: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  muted: {
    color: '#5d6c64'
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '300px minmax(0, 1fr)',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  threadList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  threadButton: {
    textTransform: 'none',
    justifyContent: 'flex-start',
    borderRadius: 10,
    border: '1px solid #dce6df',
    background: '#fff',
    padding: theme.spacing(1.5)
  },
  threadButtonActive: {
    background: '#edf7f1',
    borderColor: '#9ab9a8'
  },
  messageCard: {
    borderRadius: 10,
    border: '1px solid #dce6df',
    background: '#fff',
    padding: theme.spacing(1.5),
    maxWidth: '82%'
  },
  messageCardOffice: {
    marginLeft: 'auto',
    background: '#f0f5ff',
    borderColor: '#c7d5f2'
  },
  messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxHeight: 420,
    overflowY: 'auto',
    marginTop: theme.spacing(2),
    paddingRight: theme.spacing(0.5)
  },
  composeRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-end',
    marginTop: theme.spacing(2)
  },
  attachmentRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 8
  },
  attachmentThumb: {
    width: 64,
    height: 64,
    objectFit: 'cover',
    borderRadius: 6,
    cursor: 'pointer',
    border: '1px solid #dce6df',
    '&:hover': {
      opacity: 0.85
    }
  }
}))

function getEmployeeName (employee) {
  return employee?.displayName || [employee?.firstName, employee?.lastName].filter(Boolean).join(' ') || 'User'
}

function getThreadSubtitle (thread) {
  if (thread.type === 'crew') return 'Crew-scoped conversation'
  if (thread.type === 'direct') return 'Direct conversation'
  if (thread.type === 'job') return 'Job-scoped conversation'
  return 'Vendor-wide conversation'
}

const SubcontractorMessagingPanel = ({ vendor, employees, crews, notification }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [threads, setThreads] = useState([])
  const [selectedThreadId, setSelectedThreadId] = useState('')
  const [messages, setMessages] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sending, setSending] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [createForm, setCreateForm] = useState({
    type: 'vendor',
    crewId: '',
    employeeId: '',
    relatedJobId: '',
    relatedJobLabel: ''
  })

  const vendorId = vendor?._id
  const selectedThread = useMemo(
    () => threads.find((item) => String(item._id) === String(selectedThreadId || '')) || null,
    [selectedThreadId, threads]
  )

  const filteredEmployees = useMemo(
    () => (employees || []).filter((item) => item.subcontractorAccessStatus !== 'offboarded'),
    [employees]
  )
  const filteredCrews = useMemo(
    () => (crews || []).filter((item) => item.isActive !== false),
    [crews]
  )

  const loadThreads = useCallback(async (requestedThreadId = null) => {
    if (!vendorId) return

    setLoading(true)
    try {
      const response = await axios.get(`/api/subcontractor/v2/admin/vendors/${vendorId}/threads`)
      const items = response.data?.items || []
      setThreads(items)
      const target = items.find((item) => String(item._id) === String(requestedThreadId || selectedThreadId || '')) || items[0] || null
      setSelectedThreadId(target?._id || '')
    } catch (error) {
      console.error('Failed to load admin subcontractor threads', error)
      notification?.({
        type: 'danger',
        title: 'Messaging Load Failed',
        message: error?.response?.data?.error || 'Unable to load subcontractor message threads.'
      })
    } finally {
      setLoading(false)
    }
  }, [notification, selectedThreadId, vendorId])

  const loadMessages = useCallback(async (threadId) => {
    if (!vendorId || !threadId) {
      setMessages([])
      return
    }

    setLoadingMessages(true)
    try {
      const response = await axios.get(`/api/subcontractor/v2/admin/vendors/${vendorId}/threads/${threadId}/messages`)
      setMessages(response.data?.items || [])
    } catch (error) {
      console.error('Failed to load admin subcontractor thread messages', error)
      notification?.({
        type: 'danger',
        title: 'Message Load Failed',
        message: error?.response?.data?.error || 'Unable to load conversation history.'
      })
    } finally {
      setLoadingMessages(false)
    }
  }, [notification, vendorId])

  useEffect(() => {
    loadThreads()
  }, [loadThreads])

  useEffect(() => {
    if (selectedThreadId) {
      loadMessages(selectedThreadId)
    }
  }, [loadMessages, selectedThreadId])

  const handleCreateThread = async () => {
    try {
      const payload = {
        type: createForm.type
      }
      if (createForm.crewId) payload.crewId = createForm.crewId
      if (createForm.employeeId) payload.employeeId = createForm.employeeId
      if (createForm.relatedJobId) payload.relatedJobId = createForm.relatedJobId.trim()
      if (createForm.relatedJobLabel) payload.relatedJobLabel = createForm.relatedJobLabel.trim()

      const response = await axios.post(`/api/subcontractor/v2/admin/vendors/${vendorId}/threads`, payload)
      const threadId = response.data?.thread?._id
      setDialogOpen(false)
      setCreateForm({
        type: 'vendor',
        crewId: '',
        employeeId: '',
        relatedJobId: '',
        relatedJobLabel: ''
      })
      await loadThreads(threadId)
      notification?.({
        type: 'success',
        title: 'Thread Ready',
        message: 'The office conversation thread is ready.'
      })
    } catch (error) {
      console.error('Failed to create admin subcontractor thread', error)
      notification?.({
        type: 'danger',
        title: 'Create Failed',
        message: error?.response?.data?.error || 'Unable to create that thread.'
      })
    }
  }

  const handleSend = async () => {
    if (!selectedThread?._id || !messageText.trim()) return

    setSending(true)
    try {
      await axios.post(`/api/subcontractor/v2/admin/vendors/${vendorId}/threads/${selectedThread._id}/messages`, {
        body: messageText.trim(),
        relatedJobId: selectedThread.relatedJobId || undefined
      })
      setMessageText('')
      await Promise.all([
        loadMessages(selectedThread._id),
        loadThreads(selectedThread._id)
      ])
    } catch (error) {
      console.error('Failed to send admin subcontractor thread message', error)
      notification?.({
        type: 'danger',
        title: 'Send Failed',
        message: error?.response?.data?.error || 'Unable to send that message.'
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className={classes.wrap}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
        <div>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Office Messaging
          </Typography>
          <Typography variant="body2" className={classes.muted}>
            Send vendor-wide, crew, direct, or job-scoped in-app messages. Push, SMS, and email alerts follow each recipient&apos;s saved preferences.
          </Typography>
        </div>
        <Box display="flex" gridGap={8}>
          <Button variant="outlined" color="primary" onClick={() => loadThreads()}>
            Refresh
          </Button>
          <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
            New Thread
          </Button>
        </Box>
      </Box>

      <div className={classes.layout}>
        <div className={classes.threadList}>
          {loading ? (
            <Paper variant="outlined" style={{ padding: 16 }}>
              <Typography variant="body2" className={classes.muted}>Loading conversations...</Typography>
            </Paper>
          ) : threads.map((thread) => (
            <Button
              key={thread._id}
              className={`${classes.threadButton} ${String(thread._id) === String(selectedThreadId || '') ? classes.threadButtonActive : ''}`}
              onClick={() => setSelectedThreadId(thread._id)}
            >
              <Box width="100%">
                <Box display="flex" justifyContent="space-between" alignItems="center" gridGap={8}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#20342b' }}>
                    {thread.title || 'Conversation'}
                  </Typography>
                  <Chip size="small" label={THREAD_TYPE_LABELS[thread.type] || thread.type} />
                </Box>
                <Box mt={0.75}>
                  <Typography variant="body2" className={classes.muted}>
                    {thread.lastMessageAt ? formatPortalDateTime(thread.lastMessageAt) : 'No activity yet'}
                  </Typography>
                  <Typography variant="body2" className={classes.muted}>
                    {thread.lastMessagePreview || getThreadSubtitle(thread)}
                  </Typography>
                </Box>
              </Box>
            </Button>
          ))}
        </div>

        <div>
          {!selectedThread ? (
            <Paper variant="outlined" style={{ padding: 20 }}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>No thread selected</Typography>
              <Typography variant="body2" className={classes.muted}>
                Choose a thread from the left or create one for {vendor?.name || 'this vendor'}.
              </Typography>
            </Paper>
          ) : (
            <>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {selectedThread.title || 'Conversation'}
              </Typography>
              <Typography variant="body2" className={classes.muted}>
                {getThreadSubtitle(selectedThread)}
              </Typography>

              <div className={classes.messageList}>
                {loadingMessages ? (
                  <Paper variant="outlined" style={{ padding: 16 }}>
                    <Typography variant="body2" className={classes.muted}>Loading messages...</Typography>
                  </Paper>
                ) : messages.map((message) => (
                  <div
                    key={message._id}
                    className={`${classes.messageCard} ${message.author?.side === 'office' ? classes.messageCardOffice : ''}`}
                  >
                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#20342b' }}>
                      {message.author?.displayName || (message.author?.side === 'office' ? 'HMI Office' : 'Subcontractor')}
                    </Typography>
                    <Typography variant="caption" className={classes.muted}>
                      {formatPortalDateTime(message.createdAt)}
                      {message.priority === 'urgent' ? ' - Urgent' : ''}
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 10, color: '#24312b', whiteSpace: 'pre-wrap' }}>
                      {message.body}
                    </Typography>
                    {Array.isArray(message.attachments) && message.attachments.filter(a => a.type === 'photo' && a.url).length > 0 && (
                      <div className={classes.attachmentRow}>
                        {message.attachments.filter(a => a.type === 'photo' && a.url).map((attachment, idx) => (
                          <img
                            key={idx}
                            src={attachment.url}
                            alt={attachment.fileName || 'Photo'}
                            className={classes.attachmentThumb}
                            onClick={() => window.open(attachment.url, '_blank')}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {!loadingMessages && !messages.length ? (
                  <Paper variant="outlined" style={{ padding: 16 }}>
                    <Typography variant="body2" className={classes.muted}>
                      No messages yet. Send the first office message into this thread.
                    </Typography>
                  </Paper>
                ) : null}
              </div>

              <div className={classes.composeRow}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Office reply"
                  multiline
                  minRows={3}
                  value={messageText}
                  onChange={(event) => setMessageText(event.target.value)}
                  placeholder="Write a message to this subcontractor audience."
                />
                <Button variant="contained" color="primary" onClick={handleSend} disabled={sending || !messageText.trim()}>
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Office Thread</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Thread type"
                value={createForm.type}
                onChange={(event) => setCreateForm((current) => ({ ...current, type: event.target.value, crewId: '', employeeId: '' }))}
              >
                <MenuItem value="vendor">Vendor-wide</MenuItem>
                <MenuItem value="crew">Crew</MenuItem>
                <MenuItem value="direct">Direct user</MenuItem>
                <MenuItem value="job">Job</MenuItem>
              </TextField>
            </Grid>

            {createForm.type === 'crew' ? (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="Crew"
                  value={createForm.crewId}
                  onChange={(event) => setCreateForm((current) => ({ ...current, crewId: event.target.value }))}
                >
                  {filteredCrews.map((crew) => (
                    <MenuItem key={crew._id} value={crew._id}>{crew.name || crew.crewName}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {createForm.type === 'direct' ? (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="User"
                  value={createForm.employeeId}
                  onChange={(event) => setCreateForm((current) => ({ ...current, employeeId: event.target.value }))}
                >
                  {filteredEmployees.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>{getEmployeeName(employee)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {createForm.type === 'job' ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Related job id"
                    value={createForm.relatedJobId}
                    onChange={(event) => setCreateForm((current) => ({ ...current, relatedJobId: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Job label"
                    value={createForm.relatedJobLabel}
                    onChange={(event) => setCreateForm((current) => ({ ...current, relatedJobLabel: event.target.value }))}
                  />
                </Grid>
              </>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateThread}
            disabled={
              (createForm.type === 'crew' && !createForm.crewId) ||
              (createForm.type === 'direct' && !createForm.employeeId) ||
              (createForm.type === 'job' && !createForm.relatedJobId.trim())
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default SubcontractorMessagingPanel
